import { AvailableMedicalService } from '../../../../models/Entities/MedicalServices/AvailableMedicalService';

/*
 * Lógica pura del selector de servicios médicos con búsqueda en servidor: el estado de redux
 * (catálogo completo) puede estar vacío, así que las opciones salen de lo que devuelve el API
 * para el texto escrito y las selecciones se resuelven contra un pool local.
 */

/** Letras mínimas para pedir al servidor. */
export const MIN_QUERY_LENGTH = 2;
/** Espera tras la última tecla antes de buscar. */
export const SEARCH_DEBOUNCE_MS = 300;
/** Cuántas sugerencias se piden (o se muestran del catálogo cuando no hay texto). */
export const SUGGESTIONS_PAGE_SIZE = 25;

/** Une listas de servicios sin repetir ids; la última aparición gana. */
export const mergeServices = (...lists: Array<AvailableMedicalService[] | null | undefined>): AvailableMedicalService[] => {
  const byId = new Map<number, AvailableMedicalService>();
  lists.forEach((list) => (list || []).forEach((service) => byId.set(service.id, service)));
  return Array.from(byId.values());
};

/** Servicios del pool cuyos ids están seleccionados, en el orden de los ids; devuelve también los que faltan. */
export const resolveSelected = (
  ids: number[] | null | undefined,
  pool: AvailableMedicalService[]
): { found: AvailableMedicalService[]; missing: number[] } => {
  const found: AvailableMedicalService[] = [];
  const missing: number[] = [];
  (ids || []).forEach((id) => {
    const service = pool.find((item) => item.id === id);
    if (service) found.push(service); else missing.push(id);
  });
  return { found, missing };
};

/** Minúsculas y sin acentos. */
const fold = (text: string): string => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Filtro local (para el catálogo ya cargado) por nombre o código interno. */
export const matchesQuery = (service: Pick<AvailableMedicalService, 'serviceNameReference' | 'internalCode'>, query: string): boolean => {
  const needle = fold(query.trim());
  if (!needle) return true;
  const terms = needle.split(/\s+/);
  const haystack = fold(`${service.serviceNameReference} ${service.internalCode ?? ''}`);
  return terms.every((term) => haystack.includes(term));
};

/**
 * Opciones a mostrar: las sugerencias (del servidor o del catálogo) sin las ya elegidas,
 * ordenadas por nombre y acotadas.
 */
export const buildOptions = (
  candidates: AvailableMedicalService[],
  selectedIds: number[],
  query: string,
  limit: number = SUGGESTIONS_PAGE_SIZE
): AvailableMedicalService[] =>
  candidates
    .filter((service) => !selectedIds.includes(service.id) && matchesQuery(service, query))
    .sort((a, b) => a.serviceNameReference.localeCompare(b.serviceNameReference, 'es', { sensitivity: 'base' }))
    .slice(0, limit);

/** Texto de la opción: nombre y, si tiene, el código interno entre corchetes. */
export const optionLabel = (service: Pick<AvailableMedicalService, 'serviceNameReference' | 'internalCode'>): string =>
  service.internalCode ? `${service.serviceNameReference} [${service.internalCode}]` : service.serviceNameReference;

/** Hay que pedir al servidor: texto suficiente y distinto del último pedido. */
export const shouldSearch = (query: string, lastSearched: string | undefined): boolean => {
  const trimmed = query.trim();
  return trimmed.length >= MIN_QUERY_LENGTH && trimmed !== lastSearched;
};
